import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { isUndefined } from "lodash";
import { API_BASE_URL } from "config/site.config";
import TokenService from "./token.service";

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  headers: {
    [key: string]: any;
  };
}

class ApiService {
  private instance: any;
  private isRefreshing: boolean;
  private refreshSubscribers: ((token: any) => void)[];

  constructor() {
    this.isRefreshing = false;
    this.refreshSubscribers = [];
    this.initializeAxiosInstance();
  }

  private initializeAxiosInstance() {
    this.instance = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });

    this.setupInterceptors();
  }

  setupInterceptors() {
    this.instance.interceptors.request.use(
      (config: CustomAxiosRequestConfig) => {
        const token = TokenService.getLocalAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error: any) => {
        return Promise.reject(error);
      }
    );

    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      async (error: any) => {
        const originalRequest = error.config;
        if (
          originalRequest.url !== "/" &&
          error.response?.status === 401 &&
          isUndefined(error?.response?.data?.errorMessage) &&
          !originalRequest._retry
        ) {
          if (this.isRefreshing) {
            return new Promise((resolve) => {
              this.refreshSubscribers.push((token) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                resolve(this.instance(originalRequest));
              });
            });
          }

          this.isRefreshing = true;
          originalRequest._retry = true;

          try {
            const newToken = await this.getRefreshToken();
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            this.refreshSubscribers.forEach((callback) => callback(newToken));
            this.refreshSubscribers = [];
            return this.instance(originalRequest);
          } catch (_error: any) {
            if (
              _error?.response?.status === 500 ||
              (_error?.response?.status === 422 &&
                (_error?.response?.data?.errorMessage === "Token is expired/invalid." ||
                  _error?.response?.data?.errorMessage === "Error: Fetch multi device login."))
            ) {
              await this.handleLogout();
            }
            return Promise.reject(_error);
          } finally {
            this.isRefreshing = false;
          }
        }
        return Promise.reject(error);
      }
    );
  }

  private async handleLogout() {
    TokenService.removeUser();
    localStorage.clear();
    this.initializeAxiosInstance();
    window.location.replace("/");
  }

  async getRefreshToken() {
    const accessTokenValue = TokenService.getLocalAccessToken();
    const refreshTokenValue = TokenService.getLocalRefreshToken();
    
    if (isUndefined(accessTokenValue) || isUndefined(refreshTokenValue)) {
      throw new Error("No tokens available for refresh");
    }

    try {
      const response = await this.instance.post("/GetRefreshToken", {
        accessToken: accessTokenValue,
        refreshToken: refreshTokenValue,
      });

      const { accessToken, refreshToken, refreshExpiration } = response.data;
      
      if (accessToken && refreshToken) {
        TokenService.updateLocalAccessToken(accessToken, refreshToken, refreshExpiration);
        return accessToken;
      }
      
      throw new Error("Invalid token refresh response");
    } catch (error) {
      throw error;
    }
  }

  get(url: string, config = {}) {
    return this.instance.get(url, config);
  }

  post(url: string, data: any, config = {}) {
    return this.instance.post(url, data, config);
  }

  put(url: string, data: any, config = {}) {
    return this.instance.put(url, data, config);
  }

  delete(url: string, config = {}) {
    return this.instance.delete(url, config);
  }
}

const apiService = new ApiService();
export default apiService;